// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { UserDO } from 'wdna-components';
import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageInfo, messageError, messageBoxWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import store from '../../../../store/configure';

import ParametersEdit from '../forms/parameters/Edit';

import { parameter, ParameterDO } from '../../requests';

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;

  data: ParameterDO[];
}

export default class Parameters extends React.Component<Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    const user: UserDO = _.get(store.getState(), ['user', 'user']);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: (user.superuser === 1),
      add: false,
      del: false,
      toggleItem: true,
    };

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,

      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    this.loadingStart();
    parameter<ParameterDO[]>('list')
      ?.then((resp: ParameterDO[]) => {
        if (this.mounted) {
          this.setState({
            data: resp,
          });
        }
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public loadTab = (rowId?: number): void => {
    const tabId: string = 'PE';
    const title: string = rowId ? `Parameter Edit: ${rowId}` : 'Parameter New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: ParametersEdit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'PARAMETERS',
        title: 'Parameters',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load();
  };

  public onEdit = (value: ParameterDO): void => {
    window.store.dispatch(removeTab('SE'));
    const rowId: number = value?.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: ParameterDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      parameter('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public render(): JSX.Element {
    const { loading, data, showGrid } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      name: 'Parameter',
      value: 'Value',
    };
    const columnsToHide: string[] = [
      'description'
    ];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      value: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      name: { minWidth: 250, maxWidth: 250, },
    };

    return (
      <div id="content" className="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data}
          alias={columnAlias}
          customCellStyles={columnStyles}
          hiddenCols={columnsToHide}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
          customColumnsDefs={customColumnsDefs}
          pagination={false}
          showRowNumber
        />
      </div>
    );
  }
}
